import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { couponsApi } from '../../api/endpoints/coupons';
import { productsApi } from '../../api/endpoints/products';
import { categoriesApi } from '../../api/endpoints/categories';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Toggle } from '../../components/ui/Toggle';
import { useToast } from '../../components/ui/Alert';

const couponSchema = z.object({
  couponId: z.string().min(1, 'Required'),
  discountType: z.enum(['percentage', 'fixed']),
  amount: z.coerce.number().min(0.01, 'Required'),
  maximumAmount: z.coerce.number().min(0).optional(),
  minPurchase: z.coerce.number().min(0).optional(),
  maxUses: z.coerce.number().int().min(0).optional(),
  usesPerCustomer: z.coerce.number().int().min(0).optional(),
  expiryDate: z.string().optional(),
  startDate: z.string().optional(),
  description: z.string().optional(),
  isActive: z.boolean().optional(),
  enableAffiliate: z.boolean().optional(),
  affiliatorName: z.string().optional(),
  affiliatorEmail: z.string().email().optional().or(z.literal('')),
  affiliatorCommissionType: z.enum(['percentage', 'fixed']).optional(),
  affiliatorCommissionAmount: z.coerce.number().min(0).optional(),
  affiliatorMaxCommission: z.coerce.number().min(0).optional(),
  enableProductFilter: z.boolean().optional(),
  filterMode: z.enum(['applicable', 'excluded']).optional(),
  selectedProducts: z.array(z.string()).optional(),
  selectedCategories: z.array(z.string()).optional(),
});

type CouponFormValues = z.infer<typeof couponSchema>;

export function CouponForm() {
  const { storeId, couponId } = useParams<{ storeId: string; couponId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEdit = !!couponId && couponId !== 'create';

  const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm<CouponFormValues>({
    resolver: zodResolver(couponSchema),
    defaultValues: { discountType: 'percentage', isActive: true },
  });

  const isActive = watch('isActive');
  const showProductFilter = watch('enableProductFilter') ?? false;

  const { data: couponData } = useQuery({
    queryKey: ['coupon', storeId, couponId],
    queryFn: () => couponsApi.get(storeId!, couponId!),
    enabled: !!storeId && isEdit,
  });

  const { data: productsData } = useQuery({
    queryKey: ['products-mini', storeId],
    queryFn: () => productsApi.list(storeId!, { limit: 200 }),
    enabled: !!storeId && showProductFilter,
  });

  const { data: categoriesData } = useQuery({
    queryKey: ['categories-mini', storeId],
    queryFn: () => categoriesApi.list(storeId!),
    enabled: !!storeId && showProductFilter,
  });

  useEffect(() => {
    if (couponData?.coupon) {
      const c = couponData.coupon;
      const aff = (c as any).affiliator;
      const hasProducts = c.applicableProducts?.length;
      const hasCategories = c.applicableCategories?.length;
      reset({
        couponId: c.couponId,
        discountType: c.discountType,
        amount: c.amount,
        maximumAmount: c.maximumAmount,
        minPurchase: c.minPurchase,
        maxUses: c.maxUses,
        usesPerCustomer: c.usesPerCustomer,
        expiryDate: c.expiryDate?.split('T')[0],
        startDate: c.startDate?.split('T')[0],
        description: c.description,
        isActive: c.isActive,
        enableAffiliate: !!aff,
        affiliatorName: aff?.name ?? '',
        affiliatorEmail: aff?.email ?? '',
        affiliatorCommissionType: aff?.commissionType ?? 'percentage',
        affiliatorCommissionAmount: aff?.commissionAmount ?? 0,
        affiliatorMaxCommission: aff?.maxCommission ?? 0,
        enableProductFilter: !!(hasProducts || hasCategories),
        filterMode: 'applicable',
        selectedProducts: c.applicableProducts ?? [],
        selectedCategories: c.applicableCategories ?? [],
      });
    }
  }, [couponData, reset]);

  const buildPayload = (data: CouponFormValues) => {
    const {
      enableAffiliate, affiliatorName, affiliatorEmail, affiliatorCommissionType, affiliatorCommissionAmount, affiliatorMaxCommission,
      enableProductFilter, filterMode, selectedProducts, selectedCategories,
      ...rest
    } = data;
    const payload: Record<string, unknown> = { ...rest };
    if (data.discountType !== 'percentage') {
      delete payload.maximumAmount;
    }
    if (enableAffiliate && affiliatorName) {
      payload.affiliator = {
        name: affiliatorName,
        email: affiliatorEmail || undefined,
        commissionType: affiliatorCommissionType || 'percentage',
        commissionAmount: affiliatorCommissionAmount || 0,
        maxCommission: affiliatorCommissionType === 'percentage' && affiliatorMaxCommission ? affiliatorMaxCommission : undefined,
      };
    }
    if (enableProductFilter) {
      if (filterMode === 'excluded') {
        if (selectedProducts?.length) payload.excludedProducts = selectedProducts;
        if (selectedCategories?.length) payload.excludedCategories = selectedCategories;
        payload.applicableProducts = [];
        payload.applicableCategories = [];
      } else {
        if (selectedProducts?.length) payload.applicableProducts = selectedProducts;
        if (selectedCategories?.length) payload.applicableCategories = selectedCategories;
        payload.excludedProducts = [];
        payload.excludedCategories = [];
      }
    } else {
      payload.applicableProducts = [];
      payload.applicableCategories = [];
      payload.excludedProducts = [];
      payload.excludedCategories = [];
    }
    return payload;
  };

  const createMutation = useMutation({
    mutationFn: (data: CouponFormValues) => couponsApi.create(storeId!, buildPayload(data) as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons', storeId] });
      toast('success', t('coupons.createSuccess'));
      navigate(`/${storeId}/coupons`);
    },
    onError: () => toast('error', t('common.error')),
  });

  const updateMutation = useMutation({
    mutationFn: (data: CouponFormValues) => {
      const payload = buildPayload(data);
      delete (payload as any).couponId;
      return couponsApi.update(storeId!, couponId!, payload as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons', storeId] });
      toast('success', t('coupons.updateSuccess'));
      navigate(`/${storeId}/coupons`);
    },
    onError: () => toast('error', t('common.error')),
  });

  const onSubmit = (data: CouponFormValues) => {
    if (isEdit) updateMutation.mutate(data);
    else createMutation.mutate(data);
  };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate(`/${storeId}/coupons`)}
          className="rounded-lg p-2 text-text-muted hover:bg-bg-hover hover:text-text-primary"
        >
          <ArrowRight className="h-5 w-5" />
        </button>
        <h1 className="text-xl font-bold">
          {isEdit ? t('coupons.editCoupon') : t('coupons.addCoupon')}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
          <h2 className="text-base font-semibold mb-4">{t('coupons.details')}</h2>
          <Input
            id="couponId"
            label={t('coupons.code')}
            required
            disabled={isEdit}
            error={errors.couponId?.message}
            {...register('couponId')}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              id="discountType"
              label={t('coupons.discountType')}
              options={[
                { value: 'percentage', label: t('coupons.percentage') },
                { value: 'fixed', label: t('coupons.fixedAmount') },
              ]}
              {...register('discountType')}
            />
            <Input
              id="amount"
              label={t('coupons.amount')}
              type="number"
              step="0.01"
              error={errors.amount?.message}
              {...register('amount')}
            />
          </div>
          {watch('discountType') === 'percentage' && (
            <div>
              <Input
                id="maximumAmount"
                label={t('coupons.maximumAmount', 'Maximum Discount Amount')}
                type="number"
                step="0.01"
                min="0"
                {...register('maximumAmount')}
              />
              <p className="mt-1 text-xs text-text-muted">
                {t('coupons.maximumAmountHint', 'Cap the maximum discount value for percentage coupons')}
              </p>
            </div>
          )}
          <Textarea
            id="description"
            label={t('common.description')}
            rows={3}
            {...register('description')}
          />
        </section>

        <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
          <h2 className="text-base font-semibold mb-4">{t('coupons.limits')}</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            <Input
              id="minPurchase"
              label={t('coupons.minPurchase')}
              type="number"
              step="0.01"
              {...register('minPurchase')}
            />
            <Input
              id="maxUses"
              label={t('coupons.maxUses')}
              type="number"
              {...register('maxUses')}
            />
            <Input
              id="usesPerCustomer"
              label={t('coupons.usesPerCustomer')}
              type="number"
              {...register('usesPerCustomer')}
            />
          </div>
        </section>

        <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
          <h2 className="text-base font-semibold mb-4">{t('coupons.validity')}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input id="startDate" label={t('coupons.startDate')} type="date" {...register('startDate')} />
            <Input id="expiryDate" label={t('coupons.expiryDate')} type="date" {...register('expiryDate')} />
          </div>
          <Toggle
            label={t('coupons.active')}
            checked={isActive ?? true}
            onChange={(v) => setValue('isActive', v)}
          />
        </section>

        {/* Affiliate */}
        <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
          <Toggle
            label={t('coupons.enableAffiliate', 'Enable Affiliate')}
            checked={watch('enableAffiliate') ?? false}
            onChange={(v) => setValue('enableAffiliate', v)}
          />
          {watch('enableAffiliate') && (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  id="affiliatorName"
                  label={t('coupons.affiliateName', 'Affiliate Name')}
                  {...register('affiliatorName')}
                />
                <Input
                  id="affiliatorEmail"
                  label={t('coupons.affiliateEmail', 'Affiliate Email')}
                  type="email"
                  placeholder="affiliate@example.com"
                  {...register('affiliatorEmail')}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Select
                  id="affiliatorCommissionType"
                  label={t('coupons.commissionType', 'Commission Type')}
                  options={[
                    { value: 'percentage', label: t('coupons.percentage') },
                    { value: 'fixed', label: t('coupons.fixedAmount') },
                  ]}
                  {...register('affiliatorCommissionType')}
                />
                <Input
                  id="affiliatorCommissionAmount"
                  label={t('coupons.commissionAmount', 'Commission Amount')}
                  type="number"
                  step="0.01"
                  {...register('affiliatorCommissionAmount')}
                />
              </div>
              {watch('affiliatorCommissionType') === 'percentage' && (
                <div>
                  <Input
                    id="affiliatorMaxCommission"
                    label={t('coupons.maxCommission', 'Max Commission Amount')}
                    type="number"
                    step="0.01"
                    min="0"
                    {...register('affiliatorMaxCommission')}
                  />
                  <p className="mt-1 text-xs text-text-muted">
                    {t('coupons.maxCommissionHint', 'Cap the maximum commission for percentage-based affiliate commissions')}
                  </p>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Product / Category Filters */}
        <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
          <Toggle
            label={t('coupons.enableProductFilter', 'Restrict to specific products/categories')}
            checked={showProductFilter}
            onChange={(v) => setValue('enableProductFilter', v)}
          />
          {showProductFilter && (
            <div className="space-y-4">
              <Select
                id="filterMode"
                label={t('coupons.filterMode', 'Filter Mode')}
                options={[
                  { value: 'applicable', label: t('coupons.applicableOnly', 'Only these products/categories') },
                  { value: 'excluded', label: t('coupons.excludedOnly', 'Exclude these products/categories') },
                ]}
                {...register('filterMode')}
              />

              {/* Categories */}
              <div>
                <label className="mb-1 block text-sm font-medium text-text-secondary">
                  {t('coupons.categories', 'Categories')}
                </label>
                <div className="flex flex-wrap gap-2 rounded-lg border border-border-default bg-bg-tertiary p-2 min-h-[40px]">
                  {(categoriesData?.categories ?? []).map((cat) => {
                    const selected = (watch('selectedCategories') ?? []).includes(cat._id);
                    return (
                      <button
                        key={cat._id}
                        type="button"
                        onClick={() => {
                          const current = watch('selectedCategories') ?? [];
                          setValue('selectedCategories',
                            selected ? current.filter((id: string) => id !== cat._id) : [...current, cat._id]
                          );
                        }}
                        className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                          selected
                            ? 'bg-accent text-white'
                            : 'bg-bg-secondary text-text-secondary hover:bg-bg-hover'
                        }`}
                      >
                        {cat.name}
                      </button>
                    );
                  })}
                  {(categoriesData?.categories ?? []).length === 0 && (
                    <span className="text-xs text-text-muted">{t('common.noResults')}</span>
                  )}
                </div>
              </div>

              {/* Products */}
              <div>
                <label className="mb-1 block text-sm font-medium text-text-secondary">
                  {t('coupons.products', 'Products')}
                </label>
                <div className="flex flex-wrap gap-2 rounded-lg border border-border-default bg-bg-tertiary p-2 max-h-48 overflow-y-auto min-h-[40px]">
                  {(productsData?.products ?? []).map((prod) => {
                    const selected = (watch('selectedProducts') ?? []).includes(prod._id);
                    return (
                      <button
                        key={prod._id}
                        type="button"
                        onClick={() => {
                          const current = watch('selectedProducts') ?? [];
                          setValue('selectedProducts',
                            selected ? current.filter((id: string) => id !== prod._id) : [...current, prod._id]
                          );
                        }}
                        className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                          selected
                            ? 'bg-accent text-white'
                            : 'bg-bg-secondary text-text-secondary hover:bg-bg-hover'
                        }`}
                      >
                        {prod.title}
                      </button>
                    );
                  })}
                  {(productsData?.products ?? []).length === 0 && (
                    <span className="text-xs text-text-muted">{t('common.noResults')}</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </section>

        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={() => navigate(`/${storeId}/coupons`)}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    </div>
  );
}
